/**
 * Format identification and the capability tables from IMAGE_EDITOR_V2.md §4.
 *
 * Everything in this file is pure and synchronous — no canvas, no dynamic
 * imports — so it is safe on the page bundle's critical path and fully unit
 * tested.
 */

import { ImageFormat } from './types';

/**
 * Extension -> format. Extension wins over magic bytes (a `.jpg` that sniffs as
 * PNG is still saved as the user named it), which is what the editor's
 * "Same as original" default expects.
 */
const EXTENSION_MAP: { [ext: string]: ImageFormat } = {
    // JPEG family — the doc's row is "JPG / JPEG / JFIF".
    jpg: 'jpg', jpeg: 'jpg', jpe: 'jpg', jfif: 'jpg', jfi: 'jpg', jif: 'jpg',
    png: 'png',
    apng: 'apng',
    webp: 'webp',
    gif: 'gif',
    bmp: 'bmp', dib: 'bmp',
    tif: 'tiff', tiff: 'tiff',
    ico: 'ico',
    avif: 'avif', avifs: 'avif',
    heic: 'heic', heif: 'heic', heics: 'heic', heifs: 'heic', hif: 'heic',
    psd: 'psd', psb: 'psd',
    // The whole netpbm family maps to `ppm`; the parser then reports which
    // sub-formats it actually reads (P3/P6) instead of a useless "unknown file".
    ppm: 'ppm', pnm: 'ppm', pgm: 'ppm', pbm: 'ppm',
    eps: 'eps', epsf: 'eps', epsi: 'eps',
    // Camera RAW — server-only per §4. Kept broad on purpose: the point is to
    // show "use the converters" rather than to fail with "unknown format".
    '3fr': 'raw', arw: 'raw', bay: 'raw', cr2: 'raw', cr3: 'raw', crw: 'raw',
    dcr: 'raw', dng: 'raw', erf: 'raw', fff: 'raw', gpr: 'raw', iiq: 'raw',
    k25: 'raw', kdc: 'raw', mef: 'raw', mos: 'raw', mrw: 'raw', nef: 'raw',
    nrw: 'raw', orf: 'raw', pef: 'raw', raf: 'raw', raw: 'raw', rw2: 'raw',
    rwl: 'raw', sr2: 'raw', srf: 'raw', srw: 'raw', x3f: 'raw',
};

/** Canonical download extension per format. */
const EXTENSION_FOR: { [F in ImageFormat]: string } = {
    jpg: 'jpg',
    png: 'png',
    webp: 'webp',
    gif: 'gif',
    apng: 'apng',
    bmp: 'bmp',
    tiff: 'tiff',
    ico: 'ico',
    avif: 'avif',
    heic: 'heic',
    psd: 'psd',
    ppm: 'ppm',
    eps: 'eps',
    raw: 'raw',
    // There is no honest extension for an unidentified file. `unknown` only
    // ever reaches a download name through `encodeFallback`, which resolves it
    // to PNG, so PNG is the answer that keeps the two tables consistent.
    unknown: 'png',
};

/** §4 "decode" column: false only where the doc says server-only. */
const CANNOT_DECODE: ImageFormat[] = ['eps', 'raw'];

/** §4 "encode" column: no dependable browser encoder. */
const CANNOT_ENCODE: ImageFormat[] = ['heic', 'eps', 'raw', 'apng', 'unknown'];

/**
 * §4 "fallback" column — what "Same as original" becomes when the source format
 * cannot be written back. Formats that encode return themselves (GIF stays GIF
 * via gifenc; ICO stays ICO and the caller downsizes to <= 256 px first).
 */
const ENCODE_FALLBACK: { [F in ImageFormat]?: ImageFormat } = {
    heic: 'jpg',
    apng: 'png',
    eps: 'png',
    raw: 'png',
    unknown: 'png',
};

function extensionOf(name: string): string {
    if (!name) { return ''; }
    const clean = name.split('?')[0].split('#')[0];
    const dot = clean.lastIndexOf('.');
    if (dot < 0 || dot === clean.length - 1) { return ''; }
    return clean.slice(dot + 1).toLowerCase();
}

function ascii(head: Uint8Array, from: number, to: number): string {
    if (head.length < to) { return ''; }
    let out = '';
    for (let i = from; i < to; i++) { out += String.fromCharCode(head[i]); }
    return out;
}

function matches(head: Uint8Array, offset: number, sig: number[]): boolean {
    if (head.length < offset + sig.length) { return false; }
    for (let i = 0; i < sig.length; i++) {
        if (head[offset + i] !== sig[i]) { return false; }
    }
    return true;
}

/** HEIF brands that mean "this is a HEIC photo", per ISO/IEC 23008-12. */
const HEIF_BRANDS = ['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'hevm', 'hevs', 'msf1'];

/**
 * Magic-byte sniffing over the first 32 bytes.
 *
 * Deliberately does NOT try to tell APNG from PNG: the `acTL` chunk sits at
 * byte 33 at the earliest (8-byte signature + 25-byte IHDR), past our window,
 * and an APNG read as PNG lands on exactly the same behaviour anyway (first
 * frame in, static PNG out — §4).
 */
export function sniffFormat(head: Uint8Array): ImageFormat {
    if (!head || head.length < 2) { return 'unknown'; }

    if (matches(head, 0, [0xff, 0xd8, 0xff])) { return 'jpg'; }
    if (matches(head, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) { return 'png'; }

    const gif = ascii(head, 0, 6);
    if (gif === 'GIF87a' || gif === 'GIF89a') { return 'gif'; }

    if (ascii(head, 0, 4) === 'RIFF' && ascii(head, 8, 12) === 'WEBP') { return 'webp'; }

    // ICO/CUR: reserved(0) + type(1=ICO) + count. CUR (type 2) is not an editor
    // format, so only type 1 claims the file.
    if (matches(head, 0, [0x00, 0x00, 0x01, 0x00])) { return 'ico'; }

    // TIFF, classic and BigTIFF, both byte orders. Note many RAW files are
    // TIFF-shaped; extension detection catches those before we get here.
    if (matches(head, 0, [0x49, 0x49, 0x2a, 0x00]) || matches(head, 0, [0x4d, 0x4d, 0x00, 0x2a])
        || matches(head, 0, [0x49, 0x49, 0x2b, 0x00]) || matches(head, 0, [0x4d, 0x4d, 0x00, 0x2b])) {
        return 'tiff';
    }

    if (ascii(head, 4, 8) === 'ftyp') {
        const brand = ascii(head, 8, 12);
        if (brand === 'avif' || brand === 'avis') { return 'avif'; }
        if (HEIF_BRANDS.indexOf(brand) >= 0) { return 'heic'; }
        if (brand === 'mif1') {
            // Generic HEIF brand — both AVIF and HEIC use it as a *compatible*
            // brand. Bytes 16.. hold the compatible-brand list; AVIF wins if it
            // names itself there, otherwise treat it as HEIC.
            const compatible = ascii(head, 16, Math.min(head.length, 32));
            return compatible.indexOf('avif') >= 0 ? 'avif' : 'heic';
        }
    }

    if (ascii(head, 0, 4) === '8BPS') { return 'psd'; }

    // EPS: ASCII PostScript, or the DOS/binary EPS preview header.
    if (ascii(head, 0, 4) === '%!PS') { return 'eps'; }
    if (matches(head, 0, [0xc5, 0xd0, 0xd3, 0xc6])) { return 'eps'; }

    // netpbm: "P" + magic digit + whitespace.
    if (head[0] === 0x50 && head[1] >= 0x31 && head[1] <= 0x36) {
        const third = head.length > 2 ? head[2] : 0x0a;
        if (third === 0x20 || third === 0x09 || third === 0x0a || third === 0x0d) { return 'ppm'; }
    }

    if (ascii(head, 0, 2) === 'BM') { return 'bmp'; }

    return 'unknown';
}

/**
 * Identify a file: extension first, then magic bytes from the first 32 bytes
 * when the extension is missing or not one we know.
 *
 * `head` is optional because the common case (a named upload) never needs it;
 * `decodeImage` always reads and passes it.
 */
export function detectFormat(file: File, head?: Uint8Array): ImageFormat {
    const byExtension = EXTENSION_MAP[extensionOf(file && file.name)];
    if (byExtension) { return byExtension; }
    if (head && head.length) { return sniffFormat(head.subarray(0, 32)); }
    return 'unknown';
}

/** §4 decode column. EPS and RAW are server-only; everything else opens here. */
export function canDecodeInBrowser(f: ImageFormat): boolean {
    return CANNOT_DECODE.indexOf(f) < 0;
}

/** §4 encode column. */
export function canEncode(f: ImageFormat): boolean {
    return CANNOT_ENCODE.indexOf(f) < 0;
}

/**
 * What "Same as original" resolves to for saving. Idempotent: the result of
 * `encodeFallback` always satisfies `canEncode`.
 */
export function encodeFallback(f: ImageFormat): ImageFormat {
    return ENCODE_FALLBACK[f] || f;
}

/** Download extension, no leading dot. */
export function extensionFor(f: ImageFormat): string {
    return EXTENSION_FOR[f] || 'png';
}

/** MIME type for the formats the browser writes natively. */
export function mimeFor(f: ImageFormat): string {
    switch (f) {
        case 'jpg': return 'image/jpeg';
        case 'png': return 'image/png';
        case 'webp': return 'image/webp';
        case 'gif': return 'image/gif';
        case 'apng': return 'image/apng';
        case 'bmp': return 'image/bmp';
        case 'tiff': return 'image/tiff';
        case 'ico': return 'image/x-icon';
        case 'avif': return 'image/avif';
        case 'heic': return 'image/heic';
        case 'psd': return 'image/vnd.adobe.photoshop';
        case 'ppm': return 'image/x-portable-pixmap';
        case 'eps': return 'application/postscript';
        default: return 'application/octet-stream';
    }
}

/** Human label for messages and the save-as select. */
export function labelFor(f: ImageFormat): string {
    switch (f) {
        case 'jpg': return 'JPG';
        case 'png': return 'PNG';
        case 'webp': return 'WebP';
        case 'gif': return 'GIF';
        case 'apng': return 'APNG';
        case 'bmp': return 'BMP';
        case 'tiff': return 'TIFF';
        case 'ico': return 'ICO';
        case 'avif': return 'AVIF';
        case 'heic': return 'HEIC';
        case 'psd': return 'PSD';
        case 'ppm': return 'PPM';
        case 'eps': return 'EPS';
        case 'raw': return 'RAW';
        default: return 'this file';
    }
}
